import { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { DASHBOARD_CACHE_TTL_MS, hasCoPowers } from '@cosvision/shared';
import { useAuthStore } from './store/auth-store.js';
import { AppShell } from './components/layout/AppShell.js';
import { ProtectedRoute } from './components/routing/ProtectedRoute.js';
import { RequireCoPowers, RequireRole } from './components/routing/RoleGate.js';
import { LoginPage } from './pages/LoginPage.js';
import { RegisterPage } from './pages/RegisterPage.js';
import { EntryPage } from './pages/EntryPage.js';
import { NotFoundPage } from './pages/NotFoundPage.js';
import { Skeleton } from './components/dashboard/Skeleton.js';

// Chart-heavy pages are lazy-loaded so the operator's first paint never pulls
// Recharts. Both ride a single chunk in practice (they share the dashboard/*
// component graph) which keeps cache hits high after the first CO visit.
const DashboardPage = lazy(() =>
  import('./pages/DashboardPage.js').then((m) => ({ default: m.DashboardPage })),
);
const FundDetailPage = lazy(() =>
  import('./pages/FundDetailPage.js').then((m) => ({ default: m.FundDetailPage })),
);
const FdrPage = lazy(() => import('./pages/FdrPage.js').then((m) => ({ default: m.FdrPage })));
const ReportsPage = lazy(() =>
  import('./pages/ReportsPage.js').then((m) => ({ default: m.ReportsPage })),
);
const SettingsPage = lazy(() =>
  import('./pages/SettingsPage.js').then((m) => ({ default: m.SettingsPage })),
);
const AuditLogPage = lazy(() =>
  import('./pages/AuditLogPage.js').then((m) => ({ default: m.AuditLogPage })),
);
const EntryImportPage = lazy(() =>
  import('./pages/EntryImportPage.js').then((m) => ({ default: m.EntryImportPage })),
);
const AiKnowledgePage = lazy(() =>
  import('./pages/AiKnowledgePage.js').then((m) => ({ default: m.AiKnowledgePage })),
);
const CoOrdersPage = lazy(() =>
  import('./pages/CoOrdersPage.js').then((m) => ({ default: m.CoOrdersPage })),
);
const ApprovalsPage = lazy(() =>
  import('./pages/ApprovalsPage.js').then((m) => ({ default: m.ApprovalsPage })),
);
const PeoplePage = lazy(() =>
  import('./pages/PeoplePage.js').then((m) => ({ default: m.PeoplePage })),
);
const OfficerProfilePage = lazy(() =>
  import('./pages/OfficerProfilePage.js').then((m) => ({ default: m.OfficerProfilePage })),
);
const MyWorkPage = lazy(() =>
  import('./pages/MyWorkPage.js').then((m) => ({ default: m.MyWorkPage })),
);
const TasksPage = lazy(() =>
  import('./pages/TasksPage.js').then((m) => ({ default: m.TasksPage })),
);
const BoardPage = lazy(() =>
  import('./pages/BoardPage.js').then((m) => ({ default: m.BoardPage })),
);
const OverviewPage = lazy(() =>
  import('./pages/OverviewPage.js').then((m) => ({ default: m.OverviewPage })),
);
const TaskUpdatesPage = lazy(() =>
  import('./pages/TaskUpdatesPage.js').then((m) => ({ default: m.TaskUpdatesPage })),
);
const MessagingPage = lazy(() =>
  import('./pages/MessagingPage.js').then((m) => ({ default: m.MessagingPage })),
);
const MorePage = lazy(() => import('./pages/MorePage.js').then((m) => ({ default: m.MorePage })));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Match the backend dashboard cache TTL exactly so a stale row
      // displayed by the client never lingers past the server's freshness
      // window.
      staleTime: DASHBOARD_CACHE_TTL_MS,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function PageFallback() {
  return (
    <div className="space-y-4">
      <Skeleton className="h-6 w-64" />
      <Skeleton className="h-40 w-full" />
      <Skeleton className="h-40 w-full" />
    </div>
  );
}

function HomeRedirect() {
  const user = useAuthStore((s) => s.user);
  if (user?.role === 'DATA_ENTRY') return <Navigate to="/entry" replace />;
  // The CO and the 2IC (command authority) land on the dashboard; plain
  // officers go to their work queue.
  if (hasCoPowers(user)) return <DashboardPage />;
  if (user?.role === 'OFFICER') return <Navigate to="/my-work" replace />;
  return <DashboardPage />;
}

export function App() {
  const hydrate = useAuthStore((s) => s.hydrateFromRefresh);

  useEffect(() => {
    void hydrate();
  }, [hydrate]);

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <AppShell>
                  <Suspense fallback={<PageFallback />}>
                    <Routes>
                      <Route
                        path="/"
                        element={
                          <RequireRole allow={['CO', 'DATA_ENTRY', 'OFFICER']}>
                            <HomeRedirect />
                          </RequireRole>
                        }
                      />
                      <Route
                        path="entry"
                        element={
                          <RequireRole allow={['CO', 'DATA_ENTRY']}>
                            <EntryPage />
                          </RequireRole>
                        }
                      />
                      <Route
                        path="funds/:code"
                        element={
                          <RequireCoPowers>
                            <FundDetailPage />
                          </RequireCoPowers>
                        }
                      />
                      <Route
                        path="fdr"
                        element={
                          <RequireCoPowers>
                            <FdrPage />
                          </RequireCoPowers>
                        }
                      />
                      <Route
                        path="reports"
                        element={
                          <RequireCoPowers>
                            <ReportsPage />
                          </RequireCoPowers>
                        }
                      />
                      <Route
                        path="settings"
                        element={
                          <RequireCoPowers>
                            <SettingsPage />
                          </RequireCoPowers>
                        }
                      />
                      <Route
                        path="audit"
                        element={
                          <RequireCoPowers>
                            <AuditLogPage />
                          </RequireCoPowers>
                        }
                      />
                      <Route
                        path="entry/import"
                        element={
                          <RequireCoPowers>
                            <EntryImportPage />
                          </RequireCoPowers>
                        }
                      />
                      <Route
                        path="ai-knowledge"
                        element={
                          <RequireCoPowers>
                            <AiKnowledgePage />
                          </RequireCoPowers>
                        }
                      />
                      <Route
                        path="my-work"
                        element={
                          <RequireRole allow={['CO', 'OFFICER']}>
                            <MyWorkPage />
                          </RequireRole>
                        }
                      />
                      <Route
                        path="tasks"
                        element={
                          <RequireRole allow={['CO', 'OFFICER']}>
                            <TasksPage />
                          </RequireRole>
                        }
                      />
                      <Route
                        path="tasks/:boardId"
                        element={
                          <RequireRole allow={['CO', 'OFFICER']}>
                            <BoardPage />
                          </RequireRole>
                        }
                      />
                      <Route
                        path="overview"
                        element={
                          <RequireCoPowers>
                            <OverviewPage />
                          </RequireCoPowers>
                        }
                      />
                      <Route
                        path="task-updates"
                        element={
                          <RequireCoPowers>
                            <TaskUpdatesPage />
                          </RequireCoPowers>
                        }
                      />
                      <Route
                        path="people"
                        element={
                          <RequireRole allow={['CO', 'OFFICER']}>
                            <PeoplePage />
                          </RequireRole>
                        }
                      />
                      <Route
                        path="officers/:id"
                        element={
                          <RequireCoPowers>
                            <OfficerProfilePage />
                          </RequireCoPowers>
                        }
                      />
                      <Route
                        path="messages"
                        element={
                          <RequireRole allow={['CO', 'OFFICER']}>
                            <MessagingPage />
                          </RequireRole>
                        }
                      />
                      <Route
                        path="messages/:conversationId"
                        element={
                          <RequireRole allow={['CO', 'OFFICER']}>
                            <MessagingPage />
                          </RequireRole>
                        }
                      />
                      <Route
                        path="approvals"
                        element={
                          <RequireCoPowers>
                            <ApprovalsPage />
                          </RequireCoPowers>
                        }
                      />
                      <Route
                        path="co-orders"
                        element={
                          <RequireCoPowers>
                            <CoOrdersPage />
                          </RequireCoPowers>
                        }
                      />
                      <Route
                        path="more"
                        element={
                          <RequireRole allow={['CO', 'DATA_ENTRY', 'OFFICER']}>
                            <MorePage />
                          </RequireRole>
                        }
                      />
                      <Route path="*" element={<NotFoundPage />} />
                    </Routes>
                  </Suspense>
                </AppShell>
              </ProtectedRoute>
            }
          />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
